import React, { useCallback, useRef, useState } from "react";
import { Link } from "react-router-dom";
import styled from "styled-components";
import { FiAlertTriangle, FiAward, FiCheck, FiMoreVertical, FiPlus, FiUser, FiX } from "react-icons/fi";
import { useLanguage } from "../../../contexts/LanguageContext";
import { useDismiss } from "../../../hooks/useDismiss";
import { menuKeys } from "../../../hooks/menuKeys";
import { Num, PlayerAvatar } from "./primitives";

export type SlotMember = {
  id: string;
  name: string;
  avatar?: string;
  steamId: string;
  elo: number;
  ready: boolean;
};

/*
 * Слоты команды. Занятое место — поверхность raised; свободное — на тон темнее
 * с тонкой сплошной границей и контурным силуэтом (не ярче занятого).
 * Размеры переключаются по ширине сцены через data-size на корне.
 */
const Tile = styled.li`
  --bg: ${(p) => p.theme.colors.raised};
  position: relative;
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 8px;
  min-height: 184px;
  padding: 20px 12px 16px;
  border-radius: 12px;
  background: var(--bg);
  text-align: center;
  min-width: 0;
  [data-size="md"] & {
    min-height: 148px;
    padding: 14px 8px 12px;
    gap: 6px;
  }
  [data-size="sm"] & {
    min-height: 128px;
    padding: 12px 6px 10px;
    gap: 4px;
  }
`;
const Nick = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  max-width: 100%;
  a {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 15px;
    line-height: 20px;
    font-weight: 650;
  }
  &&& a {
    color: ${(p) => p.theme.colors.text};
  }
  a:hover {
    text-decoration: underline;
    text-underline-offset: 3px;
  }
  .you {
    flex: 0 0 auto;
    padding: 0 6px;
    border-radius: 6px;
    background: ${(p) => p.theme.colors.surfaceHover};
    color: ${(p) => p.theme.colors.textSecondary};
    font-size: 11px;
    line-height: 18px;
    font-weight: 600;
  }
  [data-size="sm"] & a {
    font-size: 13px;
    line-height: 18px;
  }
  [data-size="sm"] & .you {
    display: none;
  }
`;
const Stat = styled.div`
  display: grid;
  gap: 2px;
  justify-items: center;
  ${Num} {
    font-size: 14px;
    line-height: 20px;
    color: ${(p) => p.theme.colors.textSecondary};
  }
  .lvl {
    font-size: 12px;
    line-height: 16px;
    color: ${(p) => p.theme.colors.textMuted};
  }
  [data-size="sm"] & .lvl {
    display: none;
  }
`;
const Meter = styled.div<{ $ratio: number }>`
  width: 56px;
  height: 3px;
  border-radius: 2px;
  background: ${(p) => p.theme.colors.surfaceHover};
  overflow: hidden;
  &::after {
    content: "";
    display: block;
    width: ${(p) => p.$ratio * 100}%;
    height: 100%;
    background: ${(p) => p.theme.colors.primary};
  }
  [data-size="sm"] & {
    display: none;
  }
`;
const Tag = styled.span<{ $tone: "leader" | "ready" | "wait" | "warn" }>`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  line-height: 18px;
  font-weight: 600;
  color: ${(p) =>
    p.$tone === "leader"
      ? p.theme.colors.primary
      : p.$tone === "ready"
        ? p.theme.colors.success
        : p.$tone === "warn"
          ? p.theme.colors.warning
          : p.theme.colors.textSecondary};
  svg {
    width: 14px;
    height: 14px;
  }
  [data-size="sm"] & {
    font-size: 12px;
  }
`;
const Kebab = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  border-radius: 10px;
  color: ${(p) => p.theme.colors.textSecondary};
  &:hover,
  &[aria-expanded="true"] {
    background: ${(p) => p.theme.colors.surfaceHover};
    color: ${(p) => p.theme.colors.text};
  }
  svg {
    width: 18px;
    height: 18px;
  }
  [data-size="sm"] & {
    top: 0;
    right: 0;
    width: 36px;
    height: 36px;
  }
`;
const Menu = styled.div`
  position: absolute;
  top: 44px;
  right: 6px;
  z-index: 5;
  min-width: 200px;
  padding: 6px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.surfaceHover};
  border: 1px solid ${(p) => p.theme.colors.border};
  box-shadow: 0 16px 40px #0009;
  text-align: left;
  button {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 10px;
    min-height: 44px;
    padding: 0 10px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    color: ${(p) => p.theme.colors.text};
    &:hover:not(:disabled),
    &:focus-visible {
      background: ${(p) => p.theme.colors.input};
    }
    &.danger {
      color: ${(p) => p.theme.colors.danger};
    }
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

export function OccupiedSlot({
  m,
  isLeaderSlot,
  isSelf,
  canManage,
  locked,
  progress,
  ready,
  onMakeLeader,
  onKick,
}: {
  m: SlotMember;
  isLeaderSlot: boolean;
  isSelf: boolean;
  canManage: boolean;
  locked: boolean;
  progress?: { level: number; ratio: number; toNext: number | null };
  ready: boolean;
  onMakeLeader: () => void;
  onKick: () => void;
}) {
  const { t } = useLanguage();
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLLIElement>(null);
  const btn = useRef<HTMLButtonElement>(null);
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, close, box, btn);
  const tone = isLeaderSlot ? "leader" : !m.steamId ? "warn" : ready ? "ready" : "wait";
  return (
    <Tile ref={box}>
      <PlayerAvatar name={m.name} src={m.avatar} size={56} leader={isLeaderSlot} />
      <Nick>
        <Link to={"/profile/" + m.id} title={m.name}>
          {m.name}
        </Link>
        {isSelf && <span className="you">{t("home.you")}</span>}
      </Nick>
      <Stat title={progress && progress.toNext !== null ? t("home.to_level", { n: progress.level + 1, elo: progress.toNext }) : undefined}>
        <Num>
          {m.elo} {t("home.elo")}
        </Num>
        {progress && <span className="lvl">{t("home.level_short", { n: progress.level })}</span>}
      </Stat>
      {progress && isSelf && <Meter $ratio={progress.ratio} role="presentation" />}
      <Tag $tone={tone}>
        {isLeaderSlot ? (
          <>
            <FiAward aria-hidden="true" />
            {t("home.leader")}
          </>
        ) : !m.steamId ? (
          <>
            <FiAlertTriangle aria-hidden="true" />
            {t("home.steam_needed")}
          </>
        ) : ready ? (
          <>
            <FiCheck aria-hidden="true" />
            {t("home.ready")}
          </>
        ) : (
          t("home.not_ready")
        )}
      </Tag>
      {canManage && (
        <>
          <Kebab
            ref={btn}
            type="button"
            aria-haspopup="menu"
            aria-expanded={open}
            aria-label={t("home.member_actions", { name: m.name })}
            title={t("home.member_actions", { name: m.name })}
            onClick={() => setOpen((v) => !v)}
          >
            <FiMoreVertical aria-hidden="true" />
          </Kebab>
          {open && (
            <Menu
              role="menu"
              aria-label={t("home.member_actions", { name: m.name })}
              onKeyDown={menuKeys}
              ref={(el) => el?.querySelector<HTMLElement>('[role="menuitem"]')?.focus()}
            >
              <button
                type="button"
                role="menuitem"
                disabled={locked}
                aria-label={t("home.make_leader_named", { name: m.name })}
                onClick={() => {
                  close();
                  onMakeLeader();
                }}
              >
                <FiAward aria-hidden="true" />
                {t("home.make_leader")}
              </button>
              <button
                type="button"
                role="menuitem"
                className="danger"
                disabled={locked}
                aria-label={t("home.kick_named", { name: m.name })}
                onClick={() => {
                  close();
                  onKick();
                }}
              >
                <FiX aria-hidden="true" />
                {t("home.kick")}
              </button>
            </Menu>
          )}
        </>
      )}
    </Tile>
  );
}

const EmptyTile = styled.li`
  display: grid;
  justify-items: center;
  align-content: center;
  gap: 8px;
  min-height: 184px;
  padding: 16px 8px;
  border-radius: 12px;
  background: ${(p) => p.theme.colors.navigation};
  border: 1px solid ${(p) => p.theme.colors.border};
  color: ${(p) => p.theme.colors.textMuted};
  font-size: 13px;
  line-height: 18px;
  text-align: center;
  svg {
    width: 28px;
    height: 28px;
    color: #4a5a6c;
  }
  [data-size="md"] & {
    min-height: 148px;
  }
  [data-size="sm"] & {
    min-height: 128px;
    svg {
      width: 24px;
      height: 24px;
    }
  }
`;
const InviteTile = styled.li`
  display: contents;
  button {
    display: grid;
    justify-items: center;
    align-content: center;
    gap: 8px;
    width: 100%;
    min-height: 184px;
    padding: 16px 8px;
    border-radius: 12px;
    background: ${(p) => p.theme.colors.navigation};
    border: 1px solid ${(p) => p.theme.colors.borderControl};
    color: ${(p) => p.theme.colors.text};
    font-size: 14px;
    line-height: 20px;
    font-weight: 600;
    transition:
      background ${(p) => p.theme.motion.fast},
      border-color ${(p) => p.theme.motion.fast};
    .plus {
      display: grid;
      place-items: center;
      width: 40px;
      height: 40px;
      border-radius: 10px;
      background: ${(p) => p.theme.colors.primarySoft};
      color: ${(p) => p.theme.colors.primary};
      svg {
        width: 20px;
        height: 20px;
      }
    }
    small {
      font-size: 12px;
      line-height: 16px;
      font-weight: 500;
      color: ${(p) => p.theme.colors.textMuted};
    }
    &:hover:not(:disabled) {
      background: ${(p) => p.theme.colors.raised};
      border-color: ${(p) => p.theme.colors.primary};
    }
    &:disabled {
      cursor: not-allowed;
      border-color: ${(p) => p.theme.colors.border};
      color: ${(p) => p.theme.colors.textMuted};
    }
  }
  [data-size="md"] & button {
    min-height: 148px;
  }
  [data-size="sm"] & button {
    min-height: 128px;
    padding-inline: 4px;
    font-size: 13px;
    overflow-wrap: anywhere;
    .plus {
      width: 32px;
      height: 32px;
    }
    small {
      display: none;
    }
  }
`;

/** Свободное место без действия (нет права приглашать или идёт поиск). */
export function EmptySlot() {
  const { t } = useLanguage();
  return (
    <EmptyTile>
      <FiUser aria-hidden="true" />
      {t("home.open_slot")}
    </EmptyTile>
  );
}

/** Первое доступное место с приглашением. */
export function InviteSlot({ disabled, onClick }: { disabled: boolean; onClick: () => void }) {
  const { t } = useLanguage();
  return (
    <InviteTile>
      <button type="button" disabled={disabled} onClick={onClick}>
        <span className="plus" aria-hidden="true">
          <FiPlus />
        </span>
        {t("home.invite_player")}
        <small>{t("home.open_slot")}</small>
      </button>
    </InviteTile>
  );
}
